// Utilisateur connecté et droits, partagés par toute l'application.
import { createContext, useContext, type ReactNode } from "react";
import type { Me, Role } from "../api/types";
import { N_ } from "./i18n";

// Un client a tous les droits sur SES entrées et sorties (le serveur ne lui envoie que les siennes),
// mais aucun accès aux réglages du serveur ni aux comptes : voir isAdmin.
const RANK: Record<Role, number> = { viewer: 0, operator: 1, admin: 2, client: 2 };

export const ROLE_LABEL: Record<Role, string> = {
  viewer: N_("Lecture seule"),
  operator: N_("Opérateur"),
  admin: N_("Administrateur"),
  client: N_("Client"),
};

export const ROLE_HINT: Record<Role, string> = {
  viewer: N_("Voit les flux, l'état et le journal. Clés et mots de passe masqués."),
  operator: N_("Démarre et arrête les sorties, change leur source, déconnecte un encodeur."),
  admin: N_("Crée et supprime entrées et sorties, gère les comptes."),
  client: N_("Client extérieur : ne voit et ne gère que ses propres entrées et sorties, dans ses limites."),
};

/** Le rôle `role` a-t-il au moins les droits de `needed` (sur les flux qu'il voit) ? */
export function hasRole(role: Role, needed: Role): boolean {
  return RANK[role] >= RANK[needed];
}

/** Réglages du serveur et comptes : administrateurs seulement (jamais un client). */
export function isAdmin(role: Role): boolean {
  return role === "admin";
}

interface Session {
  me: Me;
  logout: () => void;
  /** Compte mis à jour par le serveur (double authentification activée...) : nouvelle session, WebSocket reconnecté. */
  signedIn: (me: Me) => void;
}

const AuthContext = createContext<Session | null>(null);

export function AuthProvider({ value, children }: { value: Session; children: ReactNode }) {
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useSession(): Session {
  const s = useContext(AuthContext);
  if (!s) throw new Error("useSession hors de AuthProvider");
  return s;
}

/** const can = useCan(); can("operator") */
export function useCan(): (needed: Role) => boolean {
  const { me } = useSession();
  return (needed) => hasRole(me.role, needed);
}

/** Limites du compte client connecté (null pour l'équipe : aucune restriction). */
export function useClientLimits(): Me["limits"] {
  return useSession().me.limits ?? null;
}
